import { ISettingElementV1 } from '../models/AndromeTV.types';
import { BaseCachedApi } from '../BaseCachedApi';
import { SettingsKeys } from '../models/TranslationKeys';
import { Backend } from '../Backend';
import { Log } from '@lightningjs/sdk';
import { DeviceSettingsApi } from './DeviceSettingsApi';

export class CachedNpvrSettings extends BaseCachedApi {
  protected override _cache: Map<string, ISettingElementV1> | undefined;

  /**
   * Creates an object that contains the device settings
   */
  constructor(private backend: Backend) {
    super();
    this.updateGlobalSettings();
  }

  private updateGlobalSettings() {
    this.updateExpirations(
      this.backend.globalSettings.numberValue(SettingsKeys.settingCacheExpiration) ?? 600000,
      this.backend.globalSettings.numberValue(SettingsKeys.settingCacheErrorExpiration) ?? 300000
    );
  }

  list(backend: Backend): Promise<ISettingElementV1[]> {
    const prom = this.checkCache(backend);
    return prom.then(() => {
      if (this._cache) return Array.from(this._cache.values());
      return [];
    });
  }

  setSetting(backend: Backend, settingName: string, value: string): Promise<unknown> {
    if (!backend.isAuthorized()) return Promise.resolve();

    const prom = this.checkCache(backend);

    return prom.then(() => {
      this._cache?.set(settingName, {
        name: settingName,
        value: value,
        lastUpdate: (new Date().getTime() / 1000).toString(),
      });
      if (backend.sessionInfo?.npvr) {
        return DeviceSettingsApi.update(backend, backend.sessionInfo.npvr.id, settingName, value);
      } else {
        Log.warn('[CachedDeviceSettings] setSetting: sessionInfo not set');
      }
    });
  }

  getSetting(backend: Backend, settingName: string): Promise<ISettingElementV1 | undefined> {
    const prom = this.checkCache(backend);

    return prom.then(() => {
      return this._cache?.get(settingName);
    });
  }

  deleteSetting(backend: Backend, settingName: string) {
    const prom = this.checkCache(backend);

    return prom.then(() => {
      this._cache?.delete(settingName);
      if (backend.sessionInfo?.npvr) {
        return DeviceSettingsApi.delete(backend, backend.sessionInfo.npvr.id, settingName);
      } else {
        Log.warn('[CachedDeviceSettings] deleteSetting: sessionInfo not set');
      }
    });
  }

  override fillCache(backend: Backend) {
    this.updateGlobalSettings();
    if (!backend.isAuthorized()) {
      this.clearCache();
      return Promise.resolve(new Map());
    }

    Log.info('[CachedNpvrSettings]', 'refreshing list, bypass:', this._bypassCache);
    if (!backend.sessionInfo?.npvr) {
      Log.info('[CachedNpvrSettings] fill cache: sessionInfo npvr not set');

      return new Promise(() => {
        return new Map();
      });
    }
    return DeviceSettingsApi.list(backend, backend.sessionInfo.npvr.id)
      .then(result => {
        this.clearCache();
        if (!this._cache) this._cache = new Map();
        result.forEach(element => {
          this._cache?.set(element.name, element);
        });
        return this._cache;
      })
      .catch(err => {
        Log.warn('[CachedNpvrSettings] error filling list', 'might indicate no settings set', err);
        if (!this._cache) this._cache = new Map();
        return this._cache;
      });
  }
}
